/**
 * PSTUDIO-4098. A user who opens a project they have no role on is given the
 * product-admin roles for it in place: Pro Studio calls
 * `assignProjectProductAdminRoles`, waits a fixed window, re-reads the user's
 * projects and opens the project. These are the pure decisions of that flow;
 * the timing lives in `hooks/use-access-setup.ts`, the screen in
 * `components/access-setup/`.
 *
 * Deliberately free of React, XState and design-system imports so that the
 * machine (`prostudio.xstate.ts`) and the jsdom tests can both import it.
 */

/**
 * Fixed wait between a successful role assignment and the re-read of the user's
 * projects. The role reaches the permission layer through Kafka; there is no
 * completion signal and nothing to poll, so this timer IS the contract.
 */
export const ACCESS_SYNC_WAIT_MS = 5000;

/**
 * `loading` covers the mutation, the wait and the re-read. `granted` is set when
 * the re-read list contains the project; the machine then loads it and the
 * screen is replaced by the page.
 */
export type AccessSetupStatus = 'loading' | 'granted' | 'failed' | 'denied';

/**
 * Backend `extensions.code` values that mean "this user cannot get access". The
 * decision is the backend's: the front end runs no pre-check of its own.
 */
const DENIED_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED', // no right to grant the role
  'UNPROCESSABLE', // project not found
]);

/**
 * Classify a failed `assignProjectProductAdminRoles` call. A denial is final
 * (retrying cannot help); anything else - network, 5xx, an unknown or missing
 * code, a non-error value - is `failed` and offers Retry.
 */
export const classifyAssignError = (error: unknown): 'denied' | 'failed' => {
  const graphQLErrors = (
    error as { graphQLErrors?: unknown } | null | undefined
  )?.graphQLErrors;
  if (!Array.isArray(graphQLErrors)) {
    return 'failed';
  }
  const denied = graphQLErrors.some((e) => {
    const code = (e as { extensions?: { code?: unknown } } | null)?.extensions
      ?.code;
    return typeof code === 'string' && DENIED_CODES.has(code);
  });
  return denied ? 'denied' : 'failed';
};

/**
 * Whether the re-read list names the project. An empty id names nothing: it is
 * what `ctx.project.id` holds before a route set one, and treating it as a match
 * would "grant" a project nobody asked for.
 */
export const includesProject = (
  projects: readonly { id: string }[],
  projectId: string
): boolean => !!projectId && projects.some(({ id }) => id === projectId);

/**
 * The context the `projectAccessGranted` event leaves behind (applied by the
 * `accessDenied` state of `prostudio.xstate.ts`). It installs the re-read list
 * and forgets the previously loaded project, because `#checkAccess` enters
 * `#projectFlow` only while `customerId` is undefined and `#checkingRequiredData`
 * loads solutions only while they are absent — without the reset, a user who
 * reached the denied screen by changing the project in the URL would keep the
 * PREVIOUS project's customer and solutions.
 */
export const contextAfterProjectAccessGranted = <
  Ctx extends { customerId?: unknown; solutions?: unknown },
  P,
>(
  ctx: Ctx,
  userProjects: P[]
): Ctx & { userProjects: P[] } => ({
  ...ctx,
  userProjects,
  customerId: undefined,
  solutions: undefined,
});
